/**
 * PATH: packages/drafting/src/host/useDraftingPersistence.ts
 * FILE_ID: DRAFTING-HOST-PERSIST-001
 * PURPOSE: Save / load / list Drafting Studio drawings + AI generate/refine/
 *   upload against the backend (`/api/drafting/`). Shared host hook so EVERY
 *   surface (Email Hub, main dashboard, any future hub) wires persistence the
 *   same way instead of forking a copy.
 * WHY: The studio canvas (<DraftingStudio>) stays transport-agnostic — it emits
 *   IR via onChange and takes AI handlers as props. This hook owns the HTTP.
 *   The shared @quotech/api client auto-attaches X-Organisation-ID, so every
 *   call is org-scoped without extra wiring. (Relocated from apps/email so the
 *   main dashboard reuses it — rule 12 §5: reuse before you build.)
 * FLOW:
 *   onChange(IR) → save(IR) → [POST drawing if new] → POST versions/ → refresh()
 *   open(id)     → GET drawings/{id}/ → current_ir → remount studio (studioKey++)
 *   generate/refine → ensureDrawing() → POST {id}/generate|refine/ → AiResult
 */
import { useCallback, useState } from 'react'
import { useApiClient } from '@quotech/api'
import type { DrawingIR } from '../ir/schema'
import type { AiResult, GenerateInput } from '../ai/types'

export interface SavedDrawing {
  id: string
  title: string
  status: string
  version_count: number
  updated_at: string
}

const BASE = '/api/drafting/drawings/'
const ASSETS = '/api/drafting/assets/'

/** listFrom — tolerate both a plain array and a DRF paginated `{results}`
 *  envelope so the hook is correct whether or not pagination is ever enabled. */
function listFrom(data: unknown): SavedDrawing[] {
  if (Array.isArray(data)) return data as SavedDrawing[]
  const results = (data as { results?: unknown } | null)?.results
  return Array.isArray(results) ? (results as SavedDrawing[]) : []
}

/** messageOf — pull a human message out of an axios error (DRF `detail`
 *  first, then the network message) for an inline toast. */
function messageOf(e: unknown): string {
  const ax = e as { response?: { data?: { detail?: string } }; message?: string }
  return ax?.response?.data?.detail || ax?.message || 'Request failed'
}

/** resultFrom — normalise the snake_case generate/refine response into the
 *  camelCase AiResult the studio expects. */
function resultFrom(data: unknown): AiResult {
  const d = (data ?? {}) as { ir?: DrawingIR; ok?: boolean; validation_report?: AiResult['validationReport'] }
  return { ir: (d.ir ?? {}) as DrawingIR, ok: !!d.ok, validationReport: d.validation_report }
}

export interface DraftingPersistence {
  list: SavedDrawing[]
  drawingId: string | null
  /** IR to seed the studio with on its next mount (undefined = blank). */
  loadedIr: DrawingIR | undefined
  /** Bumps on open/new so the host can remount the studio with fresh IR. */
  studioKey: number
  busy: boolean
  error: string | null
  refresh: () => Promise<void>
  save: (ir: DrawingIR) => Promise<void>
  open: (id: string) => Promise<void>
  startNew: () => void
  /** P3 AI handlers — wired into <DraftingStudio>. Each ensures a drawing
   *  exists (create-on-first-use) so generate/refine always target a row. */
  uploadSketch: (file: File) => Promise<string>
  generate: (input: GenerateInput) => Promise<AiResult>
  refine: (instruction: string, baseIr: DrawingIR) => Promise<AiResult>
}

export function useDraftingPersistence(): DraftingPersistence {
  const api = useApiClient()
  const [list, setList] = useState<SavedDrawing[]>([])
  const [drawingId, setDrawingId] = useState<string | null>(null)
  const [loadedIr, setLoadedIr] = useState<DrawingIR | undefined>(undefined)
  const [studioKey, setStudioKey] = useState(0)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const refresh = useCallback(async () => {
    try {
      const res = await api.get(BASE)
      setList(listFrom(res.data))
    } catch (e) {
      setError(messageOf(e))
    }
  }, [api])

  const save = useCallback(
    async (ir: DrawingIR) => {
      setBusy(true)
      setError(null)
      try {
        let id = drawingId
        if (!id) {
          const created = await api.post(BASE, {
            title: ir.meta.title || 'Untitled drawing',
          })
          id = String(created.data.id)
          setDrawingId(id)
        }
        // Each save is an immutable version snapshot; the backend repoints
        // current_version + bumps updated_at atomically.
        await api.post(`${BASE}${id}/versions/`, { ir, validation_report: {} })
        await refresh()
      } catch (e) {
        setError(messageOf(e))
        throw e
      } finally {
        setBusy(false)
      }
    },
    [api, drawingId, refresh],
  )

  const open = useCallback(
    async (id: string) => {
      setBusy(true)
      setError(null)
      try {
        const res = await api.get(`${BASE}${id}/`)
        setDrawingId(id)
        setLoadedIr((res.data?.current_ir as DrawingIR) ?? undefined)
        setStudioKey((k) => k + 1)
      } catch (e) {
        setError(messageOf(e))
      } finally {
        setBusy(false)
      }
    },
    [api],
  )

  const startNew = useCallback(() => {
    setDrawingId(null)
    setLoadedIr(undefined)
    setStudioKey((k) => k + 1)
  }, [])

  // ensureDrawing — generate/refine target /drawings/{id}/..., so a row must
  // exist first. Create-on-first-use and reuse the id thereafter (returns the
  // id locally to avoid racing the async setState).
  const ensureDrawing = useCallback(
    async (title?: string): Promise<string> => {
      if (drawingId) return drawingId
      const created = await api.post(BASE, { title: (title || '').slice(0, 80) || 'AI drawing' })
      const id = String(created.data.id)
      setDrawingId(id)
      return id
    },
    [api, drawingId],
  )

  const uploadSketch = useCallback(
    async (file: File): Promise<string> => {
      const fd = new FormData()
      fd.append('file', file)
      fd.append('kind', 'sketch')
      const res = await api.post(ASSETS, fd, { headers: { 'Content-Type': 'multipart/form-data' } })
      return String(res.data.id)
    },
    [api],
  )

  const generate = useCallback(
    async (input: GenerateInput): Promise<AiResult> => {
      const id = await ensureDrawing(input.prompt)
      const res = await api.post(`${BASE}${id}/generate/`, {
        prompt: input.prompt,
        scale: input.scale,
        units: input.units,
        sketch_asset_id: input.sketchAssetId,
        measurements: input.measurements,
      })
      void refresh()
      return resultFrom(res.data)
    },
    [api, ensureDrawing, refresh],
  )

  const refine = useCallback(
    async (instruction: string, baseIr: DrawingIR): Promise<AiResult> => {
      const id = await ensureDrawing(baseIr?.meta?.title)
      const res = await api.post(`${BASE}${id}/refine/`, { instruction, base_ir: baseIr })
      void refresh()
      return resultFrom(res.data)
    },
    [api, ensureDrawing, refresh],
  )

  return {
    list,
    drawingId,
    loadedIr,
    studioKey,
    busy,
    error,
    refresh,
    save,
    open,
    startNew,
    uploadSketch,
    generate,
    refine,
  }
}
